import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Alert,
  Paper,
  List,
  ListItem,
  ListItemText,
  Divider,
  CircularProgress,
} from "@mui/material";
import {
  PlayArrow as PlayIcon,
  CheckCircle as CheckIcon,
} from "@mui/icons-material";
import { apiRequest } from "../config/api";

interface MigrationResult {
  success: boolean;
  playersUpdated: number;
  leagueDaysUpdated: number;
  matchesUpdated: number;
}

interface SeasonBackfillResult {
  success: boolean;
  seasonId: string;
  leagueDaysUpdated: number;
  matchesUpdated: number;
}

function MigrationPage() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<MigrationResult | null>(null);
  const [error, setError] = useState<string>("");

  const [seasonLoading, setSeasonLoading] = useState(false);
  const [seasonResult, setSeasonResult] = useState<SeasonBackfillResult | null>(
    null,
  );
  const [seasonError, setSeasonError] = useState<string>("");

  const runMigration = async () => {
    try {
      setLoading(true);
      setError("");

      const response = await apiRequest("/api/migration/migrate-divisions", {
        method: "POST",
      });

      if (response.ok) {
        const data = await response.json();
        setResult(data.details);
      } else {
        const errorData = await response.json();
        setError(errorData.error || "Migration failed");
      }
    } catch (err) {
      setError("Network error during migration");
    } finally {
      setLoading(false);
    }
  };

  const runSeasonBackfill = async () => {
    try {
      setSeasonLoading(true);
      setSeasonError("");

      const response = await apiRequest("/api/migration/backfill-seasonid", {
        method: "POST",
        body: JSON.stringify({
          defaultSeasonId: "winter-2025-26",
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setSeasonResult(data.details);
      } else {
        const errorData = await response.json();
        setSeasonError(errorData.error || "Season backfill failed");
      }
    } catch (err) {
      setSeasonError("Network error during season backfill");
    } finally {
      setSeasonLoading(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 800, mx: "auto", p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3 }}>
        🔄 Database Migration: Multi-Division Support
      </Typography>

      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          ⚠️ Important: One-Time Migration Required
        </Typography>
        <Typography>
          This migration will add division support to your existing data. All
          current players, league days, and matches will be assigned to
          "Division 1". This process is safe and reversible, but should only be
          run once.
        </Typography>
      </Alert>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          What this migration does:
        </Typography>
        <List>
          <ListItem>
            <ListItemText
              primary="🏃‍♂️ Players"
              secondary="Adds 'division: Division 1' to all existing players"
            />
          </ListItem>
          <Divider />
          <ListItem>
            <ListItemText
              primary="📅 League Days"
              secondary="Adds 'division: Division 1' to all existing league days"
            />
          </ListItem>
          <Divider />
          <ListItem>
            <ListItemText
              primary="🏆 Matches"
              secondary="Adds 'division: Division 1' to all existing matches"
            />
          </ListItem>
        </List>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {result && (
        <Alert severity="success" sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            ✅ Migration Completed Successfully!
          </Typography>
          <List>
            <ListItem>
              <ListItemText
                primary={`${result.playersUpdated} players updated`}
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary={`${result.leagueDaysUpdated} league days updated`}
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary={`${result.matchesUpdated} matches updated`}
              />
            </ListItem>
          </List>
        </Alert>
      )}

      <Box sx={{ textAlign: "center" }}>
        <Button
          variant="contained"
          size="large"
          onClick={runMigration}
          disabled={loading || !!result}
          startIcon={
            loading ? (
              <CircularProgress size={20} />
            ) : result ? (
              <CheckIcon />
            ) : (
              <PlayIcon />
            )
          }
          sx={{ px: 4, py: 2 }}
        >
          {loading
            ? "Running Migration..."
            : result
              ? "Migration Complete"
              : "Run Migration"}
        </Button>
      </Box>

      {result && (
        <Alert severity="success" sx={{ mt: 3 }}>
          <Typography variant="h6">🎉 Ready for Multi-Division!</Typography>
          <Typography>
            Your database is now ready for multi-division support. You can now:
          </Typography>
          <List>
            <ListItem>
              <ListItemText primary="• Create players in different divisions" />
            </ListItem>
            <ListItem>
              <ListItemText primary="• Set up league days for each division" />
            </ListItem>
            <ListItem>
              <ListItemText primary="• View division-specific rankings" />
            </ListItem>
          </List>
        </Alert>
      )}

      {/* Season ID Backfill Section */}
      <Divider sx={{ my: 4 }} />

      <Typography variant="h4" sx={{ mb: 3 }}>
        🗓️ Season ID Backfill
      </Typography>

      <Alert severity="warning" sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          ⚠️ Fix Points Progress Chart Issue
        </Typography>
        <Typography>
          If your Points Progress charts are showing zeros for older league
          days, this migration will add season IDs to all league days and
          matches that don't have one. This is safe to run and will assign
          "winter-2025-26" as the default season.
        </Typography>
      </Alert>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          What this migration does:
        </Typography>
        <List>
          <ListItem>
            <ListItemText
              primary="📅 League Days"
              secondary="Adds 'seasonId: winter-2025-26' to all league days without a season"
            />
          </ListItem>
          <Divider />
          <ListItem>
            <ListItemText
              primary="🏆 Matches"
              secondary="Adds 'seasonId: winter-2025-26' to all matches without a season"
            />
          </ListItem>
        </List>
      </Paper>

      {seasonError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {seasonError}
        </Alert>
      )}

      {seasonResult && (
        <Alert severity="success" sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            ✅ Season Backfill Completed Successfully!
          </Typography>
          <List>
            <ListItem>
              <ListItemText
                primary={`${seasonResult.leagueDaysUpdated} league days updated`}
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary={`${seasonResult.matchesUpdated} matches updated`}
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary={`All assigned to season: ${seasonResult.seasonId}`}
              />
            </ListItem>
          </List>
        </Alert>
      )}

      <Box sx={{ textAlign: "center" }}>
        <Button
          variant="contained"
          size="large"
          onClick={runSeasonBackfill}
          disabled={seasonLoading || !!seasonResult}
          startIcon={
            seasonLoading ? (
              <CircularProgress size={20} />
            ) : seasonResult ? (
              <CheckIcon />
            ) : (
              <PlayIcon />
            )
          }
          sx={{ px: 4, py: 2 }}
        >
          {seasonLoading
            ? "Running Backfill..."
            : seasonResult
              ? "Backfill Complete"
              : "Run Season Backfill"}
        </Button>
      </Box>
    </Box>
  );
}

export default MigrationPage;
